// "Submit now" — one click on a claim whose readiness checklist is all green, so a
// clean claim never has to be walked to the Queue page (David's walk, 2026-09-15).
//
// What it does depends on how the practice sends claims, and it says which before it
// does anything (orchestrator ruling O12, revised):
//
//   * a QUEUE practice: readiness, then enqueue. NOTHING is transmitted — the queue is
//     the holding area a person works from, and the confirm names the queue.
//   * a STEDI practice: readiness, then the SAME single-claim submit the ledger already
//     uses (`POST /claims/submit` with one id). That transmits to the clearinghouse and
//     COSTS MONEY per submission, so the confirm says so in those words. There is no new
//     submit path here, no batching and no loop.
//
// A red readiness row disables the button; so does a claim that has already been sent or
// is already queued, and so does a request in flight — one click can only ever produce
// one submission.
//
// On staging this may be clicked only on the demo practice, and only when someone is
// watching (docs/claim-editor.md).

import React, { useState } from "react";
import { ENQUEUEABLE_STATUSES, enqueueClaim } from "../queue/api";

const API_URL = process.env.REACT_APP_API_URL || "";

// The claim is transmitted from here only for a practice Atlas submits for. Everything
// else queues.
export function submitMode({ practice, features }) {
  if (features && features.submit_queue) return "queue";
  if (practice && practice.clearinghouse === "stedi") return "stedi";
  return null;
}

export function confirmText(mode, { claimId, payerName, practiceName }) {
  if (mode === "stedi") {
    return (
      `Transmit claim #${claimId} to ${payerName || "the payer"} through the ` +
      "clearinghouse now?\n\nThis sends the claim immediately. Each submission costs " +
      "money."
    );
  }
  return (
    `Put claim #${claimId} on ${practiceName || "this practice"}'s submit queue?\n\n` +
    "Nothing is transmitted: the queue is where a person picks it up and sends it."
  );
}

async function submitToStedi(practiceId, claimId) {
  const response = await fetch(
    `${API_URL}/api/practices/${practiceId}/claims/submit`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      // One claim. The endpoint takes a list because the ledger submits a selection;
      // this is deliberately a list of one, never "submit all".
      body: JSON.stringify({ claim_ids: [claimId] }),
    },
  );
  const body = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error(body.error || "Could not submit this claim.");
  return body;
}

export default function SubmitNowButton({
  practiceId,
  practice,
  features = {},
  claim,
  readiness,
  readOnly = false,
  dirty = false,
  onDone,
}) {
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);

  const mode = submitMode({ practice, features });
  if (!mode || readOnly || !claim) return null;
  // A claim the payer already has is not submitted again from here; the ledger's
  // corrected-claim flow is the way to change one.
  if (!ENQUEUEABLE_STATUSES.includes(claim.claim_status)) return null;

  const ready = !!(readiness && readiness.ready);
  const blocked = !ready || dirty || busy;
  const label = mode === "stedi" ? "Submit now" : "Send to queue and stay here";

  const run = async () => {
    if (blocked) return;
    const ok = window.confirm(
      confirmText(mode, {
        claimId: claim.id,
        payerName: claim.payer_name,
        practiceName: practice && practice.name,
      }),
    );
    if (!ok) return;
    setBusy(true);
    setOutcome(null);
    try {
      if (mode === "stedi") {
        await submitToStedi(practiceId, claim.id);
        setOutcome({ kind: "ok", text: "Submitted." });
      } else {
        await enqueueClaim(practiceId, claim.id);
        setOutcome({ kind: "ok", text: "On the queue." });
      }
      if (onDone) onDone();
    } catch (error) {
      setOutcome({ kind: "error", text: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="ce-submit-now">
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={run}
        disabled={blocked}
        title={
          dirty
            ? "Save your edits first — what is sent is the saved claim"
            : ready
              ? mode === "stedi"
                ? "Transmit this claim to the payer now (costs money)"
                : "Put this claim on the submit queue"
              : "Fix the red rows in Ready to submit first"
        }
      >
        {busy ? "Working…" : label}
      </button>
      {outcome && (
        <span
          className={outcome.kind === "error" ? "ce-warn" : "ce-submitted"}
          role={outcome.kind === "error" ? "alert" : "status"}
        >
          {outcome.text}
        </span>
      )}
    </span>
  );
}
